import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@bb/ui';
import type { CampaignView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { CHANNEL_LABELS } from './compose/messagesDraft.ts';
import { LocalePreview } from '../composer/shared/LocalePreview.tsx';
import { TranslationStatusBadge } from '../composer/shared/TranslationStatusBadge.tsx';
import { TranslationsImportDialog } from '../composer/shared/TranslationsImportDialog.tsx';
import { useEnvironmentLocales } from '../composer/shared/useEnvironmentLocales.ts';
import { emptyFields, environmentLocales, translationStatusOf, type ChannelContent } from '../composer/shared/localization.ts';

/**
 * Translations of a campaign's messages (BB-55): the status of every locale per variant and channel, a preview by
 * locale, and the translators' CSV export and import. Shown when the environment has more than one locale; editing
 * translations in place is the composer's (BB-73, BB-97).
 */
export function CampaignTranslationsPanel({ environmentId, campaign }: { environmentId: string; campaign: CampaignView }) {
  const locales = useEnvironmentLocales(environmentId);
  const queryClient = useQueryClient();
  const canEdit = usePermissions().has('campaigns.edit');
  const [open, setOpen] = useState(false);
  if (!locales) return null;
  const { defaultLocale, codes } = environmentLocales(locales);
  if (codes.length < 2) return null;
  const others = codes.slice(1);
  const content = (campaign.draft ?? campaign.published)?.content;
  const messages = content && !Array.isArray(content.messages) ? content.messages : null;
  const rows = messages ? messages.variants.flatMap((v) => messages.channels.map((channel) => ({ variant: v, channel, content: (v.content[channel] ?? {}) as ChannelContent }))) : [];

  return (
    <section className="panel" aria-label="Translations">
      <h2>Translations</h2>
      <p className="hint">
        {campaign.draft ? 'Of the draft.' : 'Of the running version.'} Users get the translation of their locale, else its fallback, else the default ({defaultLocale}).
      </p>
      {rows.length === 0 ? (
        <p className="hint">No messages to translate yet.</p>
      ) : (
        <>
          <table className="table" aria-label="Translation status">
            <thead>
              <tr>
                <th scope="col">Variant</th>
                <th scope="col">Channel</th>
                {others.map((locale) => (
                  <th key={locale} scope="col">
                    {locale}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.variant.apiId}/${row.channel}`}>
                  <td>{row.variant.name}</td>
                  <td>{CHANNEL_LABELS[row.channel]}</td>
                  {others.map((locale) => {
                    const status = translationStatusOf(row.content, locale);
                    return (
                      <td key={locale}>
                        <TranslationStatusBadge status={status} incomplete={status === 'ready' && emptyFields(row.content, locale).length > 0} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.map((row) => (
            <details key={`${row.variant.apiId}/${row.channel}`}>
              <summary>
                Preview: {row.variant.name} · {CHANNEL_LABELS[row.channel]}
              </summary>
              <LocalePreview locales={locales} content={row.content} />
            </details>
          ))}
        </>
      )}
      <div className="actions">
        <Button onClick={() => setOpen(true)}>Import or export translations</Button>
      </div>
      <TranslationsImportDialog
        environmentId={environmentId}
        campaignId={campaign.id}
        draftVersion={campaign.draft?.version}
        canImport={canEdit}
        open={open}
        onOpenChange={setOpen}
        onImported={(result) => {
          // The answer is the campaign with its new draft; lists and revisions are read again.
          queryClient.setQueryData(['campaigns', environmentId, 'one', campaign.id], result.campaign);
          void queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId], predicate: (q) => q.queryKey[2] !== 'one' });
        }}
      />
    </section>
  );
}
